import { Trans } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useBrand, useBrands } from '@/models/brand';
import { type Product } from '@/models/product';
import { BrandInputs } from '@/routes/Requests/New/components/BrandInputs';
import { useSettingsScope } from '@/routes/SettingsBeta/SettingsContext';

export const Brand = ({
  product,
  isEditing,
  brandId,
  setBrandId,
}: {
  product: Product;
  isEditing: boolean;
  brandId: string | null;
  setBrandId: (brandId: string | null) => void;
}) => {
  if (isEditing) {
    return <BrandInputSelect brandId={brandId} setBrandId={setBrandId} />;
  }

  return <BrandReadonly product={product} />;
};

const BrandReadonly = ({ product }: { product: Product }) => {
  const { data: brand } = useBrand(product.brandId ?? undefined);

  return (
    <Stack gap="0.5rem">
      <Title as="h2" size="400" weight="medium">
        <Trans id="settings.organization.data-catalogs.products.details.brand">Brand</Trans>
      </Title>

      {brand?.name ?? (
        <Trans id="settings.organization.data-catalogs.products.details.brand.no-brands-added">
          No brands have been added yet.
        </Trans>
      )}
    </Stack>
  );
};

const BrandInputSelect = ({
  brandId,
  setBrandId,
}: {
  brandId: string | null;
  setBrandId: (brandId: string | null) => void;
}) => {
  const { scope } = useSettingsScope();
  const organizationId = scope?.scope === 'organization' ? scope.organizationId : undefined;

  const { data: { brands } = { brands: [] } } = useBrands({
    organizationId,
    limit: 500,
    offset: 0,
  });

  return (
    <Stack gap="0.5rem">
      <Title as="h2" size="400" weight="medium">
        <Trans id="settings.organization.data-catalogs.products.details.brand">Brand</Trans>
      </Title>
      <BrandInputs
        brandOptions={brands}
        selectedBrand={brands.find((brand) => brand.id === brandId)}
        onBrandSelect={(brand) => {
          setBrandId(brand?.id ?? null);
        }}
        showOtherBrandOption={false}
        showLabel={false}
      />
    </Stack>
  );
};
